import { ref, type Component } from 'vue'

export interface ModalOptions {
  title?: string
  subtitle?: string
  icon?: Component
  iconColor?: 'red' | 'blue' | 'green' | 'purple' | 'yellow' | 'gray'
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  showHeader?: boolean
  showFooter?: boolean
  showCloseButton?: boolean
  closeOnClickOutside?: boolean
}

export interface ConfirmOptions {
  type?: 'warning' | 'danger' | 'success' | 'info' | 'question'
  title?: string
  subtitle?: string
  message: string
  description?: string
  cancelText?: string
  confirmText?: string
}

export function useModal() {
  const isOpen = ref(false)
  const options = ref<ModalOptions>({})
  
  const open = (modalOptions: ModalOptions = {}) => {
    options.value = modalOptions
    isOpen.value = true
  }
  
  const close = () => {
    isOpen.value = false
  }
  
  return {
    isOpen,
    options,
    open,
    close
  }
}

// 全局确认对话框实例
let globalConfirmInstance: any = null

export function useConfirm() {
  // 如果已经有全局实例，返回它
  if (globalConfirmInstance) {
    return globalConfirmInstance
  }

  const isOpen = ref(false)
  const options = ref<ConfirmOptions>({ message: '' })
  const resolvePromise = ref<((value: boolean) => void) | null>(null)

  const confirm = (confirmOptions: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      options.value = confirmOptions
      resolvePromise.value = resolve
      isOpen.value = true
    })
  }

  const handleConfirm = () => {
    isOpen.value = false
    if (resolvePromise.value) {
      resolvePromise.value(true)
      resolvePromise.value = null
    }
  }

  const handleCancel = () => {
    isOpen.value = false
    if (resolvePromise.value) {
      resolvePromise.value(false)
      resolvePromise.value = null
    }
  }

  const instance = {
    isOpen,
    options,
    confirm,
    handleConfirm,
    handleCancel
  }

  // 设置为全局实例
  globalConfirmInstance = instance

  return instance
}

// 全局 Toast 管理
let toastInstance: any = null

export function useToast() {
  const setToastInstance = (instance: any) => {
    toastInstance = instance
  }
  
  const success = (title: string, message?: string, duration = 4000) => {
    if (toastInstance) {
      toastInstance.success(title, message, duration)
    }
  }
  
  const warning = (title: string, message?: string, duration = 5000) => {
    if (toastInstance) {
      toastInstance.warning(title, message, duration)
    }
  }
  
  const error = (title: string, message?: string, duration = 6000) => {
    if (toastInstance) {
      toastInstance.error(title, message, duration)
    }
  }
  
  const info = (title: string, message?: string, duration = 4000) => {
    if (toastInstance) {
      toastInstance.info(title, message, duration)
    }
  }
  
  return {
    setToastInstance,
    success,
    warning,
    error,
    info
  }
}
